/**
 * Storage keys used by OIDC client
 * Centralized to avoid magic strings and improve maintainability
 */
export const STORAGE_KEYS = {
  /** Key for storing login_hint in sessionStorage during auth flow */
  LOGIN_HINT: 'oidcLoginHint',
  /** Key for the idp pinned by the launch URL, kept in sessionStorage for the whole session */
  IDP: 'oidcIdp',
  /** Key for the durable copy of login_hint kept in localForage for silent renew */
  PERSISTED_LOGIN_HINT: 'login_hint',
  /** Key for storing access token in localStorage */
  TOKEN: 'whitelabel/token',
  /** Key for storing ID token */
  ID_TOKEN: 'id_token',
  /** Key for storing session ID */
  SESSION_ID: 'sessionId',
  /** Key for storing redirect URL */
  REDIRECT_URL: 'redirectUrl',
  /**
   * Prefix for the per-request code-flow transaction in sessionStorage, completed with
   * the request's `state`. Keyed by state rather than kept as a single record because a
   * login redirect and a silent renew can be in flight at once, and each needs its own
   * verifier.
   */
  AUTH_TRANSACTION_PREFIX: 'oidcTx:',
  /**
   * Key under which the refresh token waits in sessionStorage between a document being
   * discarded and the next one in the tab taking it. Never present while a page holds
   * the token — see `refresh-token.ts`.
   */
  REFRESH_TOKEN_HANDOFF: 'oidcRefreshHandoff',
  /**
   * When this tab last tried a `prompt=none` sign-in through the whole page to get a
   * refresh token (see `src/domains/auth/boot-handle.ts`). Keeps a failing one from
   * looping.
   */
  SILENT_TOP_LEVEL_AT: 'oidcSilentTopLevelAt'
} as const;

/**
 * How long an unfinished code-flow transaction stays useful. Long enough to cover a slow
 * login at the IdP (including an MFA prompt), short enough that abandoned records do not
 * accumulate in a long-lived tab.
 */
export const AUTH_TRANSACTION_TTL_MS = 10 * 60 * 1000;

/**
 * Upper bound on stored transactions. A tab that renews every ~50 minutes and fails a
 * few times would otherwise grow the list indefinitely.
 */
export const MAX_AUTH_TRANSACTIONS = 10;

/** Maximum allowed length for login_hint parameter */
export const MAX_LOGIN_HINT_LENGTH = 256;

/** Maximum allowed length for the idp parameter */
export const MAX_IDP_LENGTH = 256;

/** Shape the IdP accepts as login_hint: an e-mail address */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
